import { useEffect, useMemo, useState } from "react";
import { Plus, X } from "lucide-react";
import { api } from "../api";
import StaffLayout from "../StaffLayout";

const inputCls =
  "px-3 py-2 bg-bone border border-ink/15 focus:border-matcha-dark outline-none rounded-sm text-ink text-sm";

const VENDOR_PRESETS = [
  "Lotus",
  "Jaya Grocer",
  "Hero Supermarket",
  "Shopee",
  "Lazada",
];

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function formatErr(detail) {
  if (!detail) return "";
  if (typeof detail === "string") return detail;
  return JSON.stringify(detail);
}

export default function QuickBuy() {
  const [date, setDate] = useState(todayISO());
  const [vendor, setVendor] = useState("");
  const [customVendor, setCustomVendor] = useState("");
  const [notes, setNotes] = useState("");
  const [ingredients, setIngredients] = useState([]);
  const [lines, setLines] = useState([]); // {id, ingredient_id, qty, unit_cost, sku}
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [savedFlash, setSavedFlash] = useState("");

  const loadIngredients = async () => {
    const { data } = await api.get("/ingredients/public");
    setIngredients(data);
  };
  useEffect(() => { loadIngredients(); }, []);

  const finalVendor = vendor === "__custom" ? customVendor.trim() : vendor;

  const total = useMemo(
    () => lines.reduce((s, l) => s + (Number(l.qty || 0) * Number(l.unit_cost || 0)), 0),
    [lines]
  );

  const addLine = (ingredient_id = "") => {
    setLines((s) => [
      ...s,
      { key: Math.random().toString(36).slice(2, 9), ingredient_id, qty: "", unit_cost: "", sku: "" },
    ]);
  };
  const removeLine = (key) => setLines((s) => s.filter((l) => l.key !== key));
  const patchLine = (key, patch) =>
    setLines((s) => s.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSavedFlash("");
    if (!finalVendor) {
      setErr("Please pick or enter a vendor.");
      return;
    }
    const items = lines
      .filter((l) => l.ingredient_id && Number(l.qty) > 0 && Number(l.unit_cost) >= 0)
      .map((l) => ({
        ingredient_id: l.ingredient_id,
        qty: Number(l.qty),
        unit_cost: Number(l.unit_cost),
        sku: l.sku || "",
      }));
    if (items.length === 0) {
      setErr("Add at least one item with qty + price.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/inventory/purchases", {
        date,
        vendor: finalVendor,
        notes,
        items,
      });
      setSavedFlash(`✓ Saved ${data.created.length} item${data.created.length === 1 ? "" : "s"} from ${data.vendor}.`);
      setLines([]);
      setNotes("");
      await loadIngredients();
      setTimeout(() => setSavedFlash(""), 4000);
    } catch (e2) {
      setErr(formatErr(e2?.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const ingredientById = useMemo(
    () => Object.fromEntries(ingredients.map((i) => [i.id, i])),
    [ingredients]
  );

  return (
    <StaffLayout>
      <div className="mb-8" data-testid="quick-buy-header">
        <p className="overline text-matcha-dark mb-2">— Inventory</p>
        <h1 className="font-display text-3xl sm:text-4xl text-ink" style={{ letterSpacing: "0.04em" }}>
          Quick Buy
        </h1>
        <p className="text-ink-soft text-sm mt-2 max-w-xl">
          Pick where you bought from, then add each item with its quantity and price. Stock is updated automatically and the spend is logged for filtering later.
        </p>
      </div>

      <form onSubmit={submit} className="grid md:grid-cols-12 gap-6">
        <div className="md:col-span-8 space-y-6">
          {/* Vendor + Date */}
          <div className="bg-bone border border-ink/10 rounded-sm p-6">
            <p className="overline text-ink-soft mb-3">Where did you buy from?</p>
            <div className="flex flex-wrap gap-2 mb-4" data-testid="vendor-picker">
              {VENDOR_PRESETS.map((v) => {
                const selected = vendor === v;
                return (
                  <button
                    type="button"
                    key={v}
                    data-testid={`vendor-${v.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => setVendor(v)}
                    className={`px-4 py-2 rounded-full text-xs uppercase tracking-[0.18em] border transition-colors ${
                      selected
                        ? "bg-ink text-bone border-ink"
                        : "bg-bone text-ink border-ink/20 hover:border-ink"
                    }`}
                  >
                    {v}
                  </button>
                );
              })}
              <button
                type="button"
                data-testid="vendor-custom"
                onClick={() => setVendor("__custom")}
                className={`px-4 py-2 rounded-full text-xs uppercase tracking-[0.18em] border transition-colors ${
                  vendor === "__custom"
                    ? "bg-ink text-bone border-ink"
                    : "bg-bone text-ink border-ink/20 hover:border-ink"
                }`}
              >
                + Other
              </button>
            </div>
            {vendor === "__custom" && (
              <input
                data-testid="vendor-custom-input"
                className={inputCls + " w-full mb-4"}
                placeholder="Vendor name…"
                value={customVendor}
                onChange={(e) => setCustomVendor(e.target.value)}
              />
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline text-ink-soft block mb-2">Date</label>
                <input
                  data-testid="qb-date"
                  required type="date"
                  className={inputCls + " w-full"}
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>
              <div>
                <label className="overline text-ink-soft block mb-2">Notes (optional)</label>
                <input
                  data-testid="qb-notes"
                  className={inputCls + " w-full"}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Line items */}
          <div className="bg-bone border border-ink/10 rounded-sm p-6" data-testid="lines-card">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <p className="overline text-ink-soft">Items</p>
              <button
                type="button"
                data-testid="add-line-btn"
                onClick={() => addLine()}
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-matcha-dark hover:underline"
              >
                <Plus size={14} /> Add Line
              </button>
            </div>

            {lines.length === 0 ? (
              <div>
                <p className="text-sm text-ink-soft mb-4">Tap an item below to add it as a line, or click <span className="text-matcha-dark">+ Add Line</span> for a blank row.</p>
                <div className="flex flex-wrap gap-2" data-testid="quick-add-chips">
                  {ingredients.map((i) => (
                    <button
                      type="button"
                      key={i.id}
                      data-testid={`quick-add-${i.id}`}
                      onClick={() => addLine(i.id)}
                      className="px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.18em] bg-cream/60 border border-ink/15 text-ink hover:border-ink transition-colors"
                    >
                      {i.name} <span className="text-ink-soft">({i.unit})</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {lines.map((l) => {
                  const ing = ingredientById[l.ingredient_id];
                  const lineTotal = (Number(l.qty) || 0) * (Number(l.unit_cost) || 0);
                  return (
                    <div
                      key={l.key}
                      data-testid={`line-${l.key}`}
                      className="grid grid-cols-12 gap-2 items-start border-b border-ink/5 pb-3 last:border-0"
                    >
                      <div className="col-span-12 md:col-span-4">
                        <label className="overline text-ink-soft block mb-1 md:hidden">Item</label>
                        <select
                          data-testid={`line-item-${l.key}`}
                          required
                          className={inputCls + " w-full"}
                          value={l.ingredient_id}
                          onChange={(e) => patchLine(l.key, { ingredient_id: e.target.value })}
                        >
                          <option value="">— Select item —</option>
                          {ingredients.map((i) => (
                            <option key={i.id} value={i.id}>{i.name} ({i.unit})</option>
                          ))}
                        </select>
                        {ing && (
                          <p className="text-[10px] text-ink-soft mt-1">Stock: {ing.stock_qty} {ing.unit}</p>
                        )}
                      </div>
                      <div className="col-span-4 md:col-span-2">
                        <label className="overline text-ink-soft block mb-1 md:hidden">Qty</label>
                        <input
                          data-testid={`line-qty-${l.key}`}
                          type="number" step="0.001" min="0" required
                          placeholder="Qty"
                          className={inputCls + " w-full text-right"}
                          value={l.qty}
                          onChange={(e) => patchLine(l.key, { qty: e.target.value })}
                        />
                      </div>
                      <div className="col-span-4 md:col-span-2">
                        <label className="overline text-ink-soft block mb-1 md:hidden">Price (RM)</label>
                        <input
                          data-testid={`line-price-${l.key}`}
                          type="number" step="0.01" min="0" required
                          placeholder="Price"
                          className={inputCls + " w-full text-right"}
                          value={l.unit_cost}
                          onChange={(e) => patchLine(l.key, { unit_cost: e.target.value })}
                        />
                      </div>
                      <div className="col-span-3 md:col-span-2">
                        <label className="overline text-ink-soft block mb-1 md:hidden">SKU</label>
                        <input
                          data-testid={`line-sku-${l.key}`}
                          placeholder="SKU"
                          className={inputCls + " w-full"}
                          value={l.sku}
                          onChange={(e) => patchLine(l.key, { sku: e.target.value })}
                        />
                      </div>
                      <div className="col-span-1 md:col-span-1 text-right pt-2">
                        <span className="text-xs text-matcha-dark whitespace-nowrap">RM {lineTotal.toFixed(2)}</span>
                      </div>
                      <div className="col-span-1 md:col-span-1 flex justify-end pt-2">
                        <button
                          type="button"
                          data-testid={`line-remove-${l.key}`}
                          onClick={() => removeLine(l.key)}
                          className="text-ink-soft hover:text-red-600"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                  );
                })}
                <div className="pt-3">
                  <button
                    type="button"
                    onClick={() => addLine()}
                    className="text-xs uppercase tracking-[0.18em] text-matcha-dark hover:underline inline-flex items-center gap-2"
                  >
                    <Plus size={14} /> Add Another Line
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Summary */}
        <div className="md:col-span-4">
          <div className="bg-bone border border-ink/10 rounded-sm p-6 sticky top-6" data-testid="qb-summary">
            <p className="overline text-matcha-dark mb-3">Summary</p>
            <ul className="space-y-2 text-sm mb-4">
              <li className="flex justify-between">
                <span className="text-ink-soft">Vendor</span>
                <span className="text-ink text-right">{finalVendor || "—"}</span>
              </li>
              <li className="flex justify-between">
                <span className="text-ink-soft">Date</span>
                <span className="text-ink">{date}</span>
              </li>
              <li className="flex justify-between">
                <span className="text-ink-soft">Items</span>
                <span className="text-ink">{lines.filter((l) => l.ingredient_id && Number(l.qty) > 0).length}</span>
              </li>
            </ul>
            <div className="border-t border-ink/10 pt-4 mb-4">
              <p className="overline text-ink-soft">Total</p>
              <p className="font-display text-3xl text-matcha-dark" style={{ letterSpacing: "0.02em" }} data-testid="qb-total">
                RM {total.toFixed(2)}
              </p>
            </div>
            {err && <p className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-sm mb-3">{err}</p>}
            {savedFlash && <p className="text-sm text-matcha-dark bg-sage/30 px-3 py-2 rounded-sm mb-3" data-testid="qb-saved-flash">{savedFlash}</p>}
            <button
              data-testid="qb-submit"
              type="submit"
              disabled={busy || lines.length === 0 || !finalVendor}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 bg-ink text-bone text-xs uppercase tracking-[0.22em] hover:bg-matcha-dark transition-colors disabled:opacity-50"
            >
              {busy ? "Saving…" : "Save Purchase"}
            </button>
            <p className="text-[10px] text-ink-soft mt-3 text-center">
              Visible later on <span className="text-matcha-dark">Catalog → Purchases</span>
            </p>
          </div>
        </div>
      </form>
    </StaffLayout>
  );
}
